import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { SubSink } from "subsink";
import { UserService } from "../../../../../core/user/user.service";
import { DynamicControl } from "../../../../../dynamic-forms/dynamic-control.model";
import { FormService } from "../../../../../dynamic-forms/form.service";
import { Checkbox } from "../../../../../dynamic-forms/inputs/checkbox/checkbox.model";
import { Switch } from "../../../../../dynamic-forms/inputs/switch/switch.model";
import { GridColumnDefinition } from "../../../../../shared/grid/models/grid-column-definition.model";
import { GridConfiguration } from "../../../../../shared/grid/models/grid-configuration.model";
import { ArrayHelper } from "../../../../../utilities/contracts/array-helper";
import { NumberHelper } from "../../../../../utilities/contracts/number-helper";
import { StringHelper } from "../../../../../utilities/contracts/string-helper";
import { ProjectConfigurationService } from "../project-config.service";

@Component({
  selector: "app-hedis-eve-settings",
  templateUrl: "./hedis-eve-settings.component.html",
  styleUrls: ["./hedis-eve-settings.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HedisEveSettingsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projectConfigForm: FormGroup;
  @Input() projectAttributes: any;
  @Input() projectId: number;
  @Input() isHedisProject = false;
  @Input() chaseCount: number;

  // EVE for Abstraction switch and the "select all" checkbox above the grid.
  formGroup: FormGroup;
  // One parent checkbox and one AI Assist checkbox per measure; rebuilt whenever the measures or settings reload.
  abstractionFormGroup: FormGroup;
  firstPassBotEnabled: Switch;
  allSelectionCheckbox: Checkbox;
  abstractionModels: DynamicControl[] = [];
  gridConfiguration = new GridConfiguration();
  hedisFirstPassBotEnabled = false;
  readonly helperText = "Contact your Customer Success Manager to enable EVE for First Pass Coding.";
  private measures: any[] = [];
  private firstPassBotSetting = null;
  private botFirstPassAttributeData: any[] = [];
  private rowType = false;
  private rowIndex = 6;
  private sink = new SubSink();

  constructor(
    private readonly formService: FormService,
    private readonly userService: UserService,
    private readonly service: ProjectConfigurationService,
    private readonly changeDetector: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.hedisFirstPassBotEnabled = this.userService.getUserToken().enableHedisFirstPassBot;
    this.createForm();
    this.createGrid();
    this.projectConfigForm.addControl("hedisEveSettingsFormGroup", this.formGroup);
    this.projectConfigForm.addControl("hedisEveAbstractionFormGroup", this.abstractionFormGroup);
    this.applyChaseCountRestriction();
    this.getMeasures();

    if (this.projectAttributes) {
      this.loadHedisEveSettings();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.formGroup) {
      return;
    }

    if (changes.chaseCount) {
      this.applyChaseCountRestriction();
    }
    if (changes.projectAttributes && this.projectAttributes) {
      this.loadHedisEveSettings();
    }
  }

  ngOnDestroy(): void {
    this.sink.unsubscribe();
  }

  get isFirstPassBotEnabled(): boolean {
    return Number(this.formGroup.get("firstPassBotEnabled").value) === 1;
  }

  get isGridEnabled(): boolean {
    return this.hedisFirstPassBotEnabled && this.isHedisProject && this.isFirstPassBotEnabled;
  }

  saveTo(projectAttributes: any): void {
    if (!projectAttributes.FirstPassBotEnabled) {
      return;
    }

    projectAttributes.FirstPassBotEnabled.attributeValue = Number(this.formGroup.get("firstPassBotEnabled").value);
    if (projectAttributes.FirstPassBotEnabled.attributeValue === 1 && this.hasAbstractionControls) {
      Object.keys(this.abstractionFormGroup.controls).forEach(key => {
        const keySplitValues = key.toString().split("_");
        const measureCode = keySplitValues[0];
        const compliance = keySplitValues[1];
        const complianceKey = StringHelper.isAvailable(compliance) ? `${measureCode}_${compliance}` : "";
        const checkIfSameMeasureExist = this.botFirstPassAttributeData.filter(x => x.measureCode === measureCode);
        if (!ArrayHelper.isAvailable(checkIfSameMeasureExist)) {
          this.botFirstPassAttributeData.push({
            measureCode,
            aiassist: StringHelper.isAvailable(complianceKey)
              ? NumberHelper.isAvailable(this.abstractionFormGroup.controls[complianceKey].value)
                ? this.abstractionFormGroup.controls[complianceKey].value ? 1 : 0
                : 0
              : 0,
          });
        } else if (compliance === "aiassist") {
          checkIfSameMeasureExist[0].aiassist = (this.abstractionFormGroup.controls[complianceKey].value !== null)
            ? this.abstractionFormGroup.controls[complianceKey].value : 0;
        }
      });

      if (projectAttributes.FirstPassBotSetting) {
        projectAttributes.FirstPassBotSetting.attributeValue = JSON.stringify({
          firstpassbotsettings: Array.prototype.concat(this.botFirstPassAttributeData),
        });
      }
    }
  }

  onChangeFirstPassBot(): void {
    if (this.isFirstPassBotEnabled && this.isHedisProject && this.hasMeasures) {
      this.createAbstractionControls();
    }
  }

  onAllSelection(): void {
    const value = this.formGroup.get("allSelectionCheckbox").value ? 1 : null;
    this.formGroup.get("allSelectionCheckbox").setValue(value);
    Object.keys(this.abstractionFormGroup.controls).forEach(key => {
      this.abstractionFormGroup.controls[key].setValue(value);
    });
  }

  onSelectFirstPassChange(event: any, model: any): void {
    const isCheckedValue = event.checked ? 1 : 0;

    Object.keys(this.abstractionFormGroup.controls).forEach(key => {
      const keySplitValues = key.toString().split("_");
      const measureCode = keySplitValues[0];
      const compliance = keySplitValues[1];
      const complianceKey = StringHelper.isAvailable(compliance) ? `${model.key}_${compliance}` : "";
      const selectedParent = model.parent;
      if (model.key === key || complianceKey === key) {
        if (isCheckedValue === 1) {
          this.abstractionFormGroup.controls[key].setValue(isCheckedValue);
          if (StringHelper.isAvailable(compliance) && measureCode === model.parent
            && this.abstractionFormGroup.controls[`${selectedParent}_aiassist`].value === 1
          ) {
            this.abstractionFormGroup.controls[measureCode].setValue(isCheckedValue);
          }
        } else if (isCheckedValue === 0 || model.key.split("_")[0] === measureCode) {
          this.abstractionFormGroup.controls[measureCode].setValue(isCheckedValue);
          this.abstractionFormGroup.controls[key].setValue(isCheckedValue);
        }
      }
    });

    this.syncAllSelectionCheckbox();
  }

  // Alternates the row background every two controls (measure checkbox + its hidden AI Assist checkbox).
  getRowClass(index: number, formModelCount: number): string {
    let gridClass = "form-factory";
    if (index === 0) {
      this.rowIndex = 1;
    }

    if (index === this.rowIndex + 1) {
      this.rowIndex += 2;
      this.rowType = !this.rowType;
    }

    gridClass += this.rowType ? " even-row" : " odd-row";
    if (index === formModelCount - 1) {
      this.rowType = false;
    }
    return gridClass;
  }

  getAssistClass(model: any): string {
    return model?.key?.includes("_aiassist") ? "aiassist" : "";
  }

  trackByIndex(index: number): number {
    return index;
  }

  private get hasMeasures(): boolean {
    return ArrayHelper.isAvailable(this.measures);
  }

  private get hasAbstractionControls(): boolean {
    return ArrayHelper.isAvailable(Object.keys(this.abstractionFormGroup.controls));
  }

  private createForm(): void {
    this.firstPassBotEnabled = new Switch({
      key: "firstPassBotEnabled",
      label: "EVE for Abstraction",
      disabled: !this.hedisFirstPassBotEnabled,
      preceedingOffLabel: "Disable",
      followingOnLabel: "Enable",
      tooltip: "EVE is your virtual team member. EVE (Evidence Validation Engine) is an AI-infused tool that assists in closing care gaps.",
    });

    this.allSelectionCheckbox = new Checkbox({
      key: "allSelectionCheckbox",
    });

    this.formGroup = this.formService.createFormGroup([
      this.firstPassBotEnabled,
      this.allSelectionCheckbox,
    ]);
    this.abstractionFormGroup = new FormGroup({});
  }

  private createGrid(): void {
    this.gridConfiguration.columns = [
      new GridColumnDefinition({ header: "AI Assist", width: "30.3%" }),
      new GridColumnDefinition({ header: "Measure", isSortableColumn: false, width: "28.3%" }),
    ];
    this.gridConfiguration.hasPagination = false;
    this.gridConfiguration.showExportAction = false;
    this.gridConfiguration.showActionColumn = false;
    this.gridConfiguration.showMenu = false;
  }

  private getMeasures(): void {
    this.sink.add(this.service
      .getAllMeasureResult(this.projectId)
      .subscribe(items => {
        this.measures = items as any;

        if (this.projectAttributes != null && this.hasMeasures) {
          this.loadHedisEveSettings();
        }
        this.changeDetector.markForCheck();
      }));
  }

  private loadHedisEveSettings(): void {
    if (this.projectAttributes.FirstPassBotSetting) {
      this.firstPassBotSetting = JSON.parse(this.projectAttributes.FirstPassBotSetting.attributeValue);
    }

    this.formGroup.patchValue({
      firstPassBotEnabled: Number(this.projectAttributes.FirstPassBotEnabled?.attributeValue),
    });

    if (this.hasMeasures) {
      this.applyServiceOrgRestriction();
      this.createAbstractionControls();
    }
    this.changeDetector.markForCheck();
  }

  // EVE for Abstraction is locked once the project has chases.
  private applyChaseCountRestriction(): void {
    if (NumberHelper.isGreaterThan(this.chaseCount, 0)) {
      this.formGroup.get("firstPassBotEnabled").disable();
      this.changeDetector.markForCheck();
    }
  }

  // Service orgs without the HEDIS first pass bot feature always save EVE for Abstraction as off.
  private applyServiceOrgRestriction(): void {
    if (!this.hedisFirstPassBotEnabled) {
      const control = this.formGroup.get("firstPassBotEnabled");
      control.setValue(0);
      control.disable();
    }
  }

  private createAbstractionControls(): void {
    this.abstractionModels = this.measures.reduce(this.toAbstractionControls.bind(this), []);
    this.abstractionFormGroup = this.formService.createFormGroup(this.abstractionModels);
    this.projectConfigForm.setControl("hedisEveAbstractionFormGroup", this.abstractionFormGroup);

    const controlsArray = Object.values(this.abstractionFormGroup.controls);
    if (controlsArray.every(control => control.value === 1)) {
      this.formGroup.get("allSelectionCheckbox").setValue(1);
    }
  }

  private toAbstractionControls(controls: DynamicControl[], measure: any): DynamicControl[] {
    // Last match wins, as it did before the extraction.
    const savedSetting = (this.firstPassBotSetting?.firstpassbotsettings ?? [])
      .filter(values => values.MeasureCode === measure.measureCode)
      .pop();
    const hasComplianceValue = savedSetting != null;

    controls.push(
      new Checkbox({
        key: measure.measureCode,
        label: measure.measureCode,
        value: hasComplianceValue && savedSetting.AIAssist === 1 ? 1 : 0,
        parent: null,
      }));
    controls.push(
      new Checkbox({
        key: `${measure.measureCode}_aiassist`,
        value: hasComplianceValue ? savedSetting.AIAssist : 0,
        parent: measure.measureCode,
      }));
    return controls;
  }

  private syncAllSelectionCheckbox(): void {
    const controlsArray = Object.values(this.abstractionFormGroup.controls);
    const allSelected = controlsArray.every(control => control.value === 1);
    this.formGroup.get("allSelectionCheckbox").setValue(allSelected ? 1 : null);
  }
}
